/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get, includes, keys, set } from 'lodash';
import { Grid, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/system';
import { useNavigate } from 'react-router';
import {
  OrderPriceAccumulator,
  ShipmentPrice,
} from '@dpdgroupuk/consumer-shipping-helper';
import useAnalytics from '../../hooks/useAnalytics';
import useRecaptcha from '../../hooks/useRecaptcha';
import {
  BasketItemHelpers,
  NetworkHelpers,
  ShipmentHelpers,
} from '../../helpers';
import BasketItemsGroupsList from '../../components/BasketItemsGroupsList';
import { SummaryDetails } from '../../components/SummaryDetails';
import Voucher from '../../features/Voucher';
import SalesConditions from '../../components/SalesConditions';
import InformationCollecting from '../../components/InformationCollecting';
import ConfirmDialog from '../../components/modals/ConfirmModal';
import useBreakpoint from '../../hooks/useBreakpoint';
import useGetBillingAddress from '../../hooks/useGetBillingAddress';
import { BasketActions, BasketSelectors } from '../../redux/basket';
import PurchaseWidget from '../../components/PurchaseWidget';
import { useOverlay } from '../../features/Overlay';
import { useCustomSnackbar } from '../../features/CustomSnackbar';
import ConfigSlice from '../../redux/configSlice';
import Loader from '../../features/Loader';
import { ANALYTICS, FORM, POINT_TYPE, STRINGS } from '../../constants';
import { countriesSelectors } from '../../redux/countriesSlice';
import { validateFullShipmentSync } from '../Order/helpers/shipment';
import { PICKUP_STATUS_ACTIVE } from '../Order/constants';
import { SHIPMENT_FIELDS } from '../../constants/forms';
import { IossErrorModal } from '../../components/IossErrorModal';
import BillingAddress from '../../components/BillingAddress';
import addressBooksSlice from '../../redux/addressBooksSlice';
import useAutoReloadOnInactivity from '../../hooks/useAutoReloadOnInactivity';
import { getAvailableNetworks } from '../Order/pages/ParcelDetails/helpers';
import { getNetworksByParcelSizeAndWeight } from '../../helpers/network';
import BasketCard from './components/BasketCard';
import BasketViewSlice, { validateCollectionDateAsync } from './slice';

const StyledGrid = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  position: 'sticky',
  top: `calc(var(--dpd-appbar-height, 0px) + ${theme.spacing(2)})`,
  height: '100%',
  marginTop: theme.spacing(4),
}));

const Basket = () => {
  useAutoReloadOnInactivity();
  const { executeRecaptchaV3 } = useRecaptcha();

  const dispatch = useDispatch();
  const [selectedBillingBasketItem, setSelectedBillingBasketItem] = useState(
    () => localStorage.getItem('selectedBillingAddressId') || ''
  );
  const basketId = useSelector(BasketSelectors.getBasketId);
  const basketItems = useSelector(BasketSelectors.getBasketItems);
  const checkoutList = useSelector(BasketViewSlice.selectors.getCheckoutList);
  const threeDSecureData = useSelector(
    BasketViewSlice.selectors.getThreeDSecureData
  );
  const [errors, setErrors] = useState({});
  const [openIossErrorModal, setOpenIossErrorModal] = useState(false);
  const [IossRuleBasketItemId, setIossRuleBasketItemId] = useState(null);
  const countries = useSelector(countriesSelectors.getCountries);
  const defaultCutOff = useSelector(
    ConfigSlice.selectors.getCollectionCutOffConfig
  );

  const { terms: termsLink } = useSelector(
    ConfigSlice.selectors.getLinksConfig
  );
  const { voucher: displayVoucher } = useSelector(
    ConfigSlice.selectors.getFeatureConfig
  );
  const priceConfig = useSelector(ConfigSlice.selectors.getPriceConfig);

  const defaultBillingAddressBook = useSelector(
    addressBooksSlice.selectors.getBillingAddressBook
  );

  const isSmallScreen = useBreakpoint('sm');
  const theme = useTheme();
  const { setInterfaceId, Trackable } = useAnalytics();
  const [acceptTerms, setAcceptTerms] = useState(false);
  const references = useSelector(BasketViewSlice.selectors.getBasketReferences);
  const overlay = useOverlay();
  const snackbar = useCustomSnackbar();
  const navigate = useNavigate();
  const [voucher, setVoucher] = useState();
  const [selectedBasketItem, setSelectedBasketItem] = useState();
  const receiverContactName = ShipmentHelpers.getReceiverContactName(
    selectedBasketItem?.shipment
  );
  const parcels = get(
    selectedBasketItem,
    `shipment.${FORM.SHIPMENT_FIELDS.PARCELS.KEY}`,
    []
  );

  const isCheckoutButtonDisabled =
    !acceptTerms ||
    !checkoutList.length ||
    (!selectedBillingBasketItem && !defaultBillingAddressBook);

  const onAddToCheckout = useCallback(
    basketItemId => {
      dispatch(
        BasketViewSlice.actions.setCheckoutList([...checkoutList, basketItemId])
      );
    },
    [checkoutList, dispatch]
  );

  const onRemoveFromCheckout = useCallback(
    basketItemIdToRemove => {
      const newCheckoutList = checkoutList.filter(
        basketItemId => basketItemId !== basketItemIdToRemove
      );
      return dispatch(BasketViewSlice.actions.setCheckoutList(newCheckoutList));
    },
    [checkoutList, dispatch]
  );

  const onDeleteBasketItem = useCallback(async () => {
    try {
      overlay.show();
      setSelectedBasketItem(null);

      await dispatch(
        BasketActions.deleteBasketItem(selectedBasketItem.basketItemId)
      ).unwrap();
      onRemoveFromCheckout(selectedBasketItem.basketItemId);
      const newBasketItems = await dispatch(
        BasketActions.getBasketItems()
      ).unwrap();

      if (newBasketItems.length === 1) {
        const lastBasketItemId = newBasketItems[0].basketItemId;

        if (
          !checkoutList.includes(lastBasketItemId) &&
          !errors[lastBasketItemId]
        ) {
          dispatch(BasketViewSlice.actions.setCheckoutList([lastBasketItemId]));
        }
      }
    } catch (e) {
      snackbar.showError({
        message: STRINGS.FAILED_TO_DELETE_BASKET_ITEM,
      });
    } finally {
      overlay.hide();
    }
  }, [
    selectedBasketItem,
    checkoutList,
    dispatch,
    errors,
    onRemoveFromCheckout,
    overlay,
    snackbar,
  ]);

  const checkoutListPrice = useMemo(() => {
    // totals section
    const totals = checkoutList.reduce((acc, id) => {
      const basketItem = basketItems.find(
        basketItem => basketItem.basketItemId === id
      );
      const reference = references[basketItem?.basketItemId];

      if (!reference || !basketItem) {
        return acc;
      }

      return OrderPriceAccumulator.aggregate(
        acc,
        id,
        ShipmentPrice.calculateShipmentPrice(
          basketItem.shipment,
          reference.networkPrice,
          priceConfig
        )
      );
    }, new OrderPriceAccumulator());

    // voucher section
    if (voucher) {
      totals.applyVoucherDiscount(voucher);
    }

    return totals.toViewJSON(2);
  }, [checkoutList, references, voucher, priceConfig, basketItems]);

  const basketItemsWithPrice = BasketItemHelpers.getBasketItemsWithPrice(
    checkoutList,
    basketItems,
    checkoutListPrice
  );

  const getBillingAddress = useGetBillingAddress();

  useEffect(() => {
    setInterfaceId(ANALYTICS.BASKET.INTERFACE_ID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem('selectedBillingAddressId', selectedBillingBasketItem);
  }, [selectedBillingBasketItem]);

  const checkPickupLocationStatus = useCallback(
    (basketItem, shipment, itemReferences, initialErrors) => {
      const networkCode = get(shipment, FORM.SHIPMENT_FIELDS.NETWORK_CODE.KEY);
      const pickupLocationCode =
        shipment.outboundConsignment?.pickupDetails?.pickupLocationCode;

      if (
        networkCode &&
        NetworkHelpers.removeBusinessUnit(networkCode) ===
          STRINGS.NETWORK_SHIP_TO_SHOP_KEY &&
        pickupLocationCode
      ) {
        const pickupLocationStatus = get(
          itemReferences,
          `deliveryPickup.pickupLocation.pickupLocationStatus`
        );

        if (pickupLocationStatus !== PICKUP_STATUS_ACTIVE) {
          set(initialErrors, basketItem.basketItemId, {
            pickupLocationError: true,
          });
        }
      }
    },
    []
  );

  const validateCollectionDate = useCallback(
    async (shipment, errors) => {
      if (
        !errors.includes(STRINGS.INVALID_COLLECTION_DATE) &&
        get(shipment, FORM.SHIPMENT_FIELDS.COLLECT_FROM.KEY) === POINT_TYPE.DOOR
      ) {
        try {
          const isValidCollectionDate = await dispatch(
            validateCollectionDateAsync({ values: shipment, defaultCutOff })
          ).unwrap();

          if (!isValidCollectionDate) {
            errors.push(STRINGS.INVALID_COLLECTION_DATE);
          }
        } catch (error) {
          snackbar.showError({
            // TODO: put correct message later
            message: 'Error validating collection date',
          });
        }
      }
    },
    [defaultCutOff, dispatch, snackbar]
  );

  const validateNetwork = useCallback((shipment, itemReferences, errors) => {
    const networkCode = get(shipment, FORM.SHIPMENT_FIELDS.NETWORK_CODE.KEY);
    const availableNetworks = getAvailableNetworks(
      getNetworksByParcelSizeAndWeight(itemReferences.networks, shipment),
      shipment
    );
    if (
      !networkCode ||
      !availableNetworks.length ||
      availableNetworks.findIndex(
        network => network.networkKey === networkCode
      ) === -1
    ) {
      errors.push(STRINGS.INVALID_NETWORK_SELECTED);
    }
  }, []);

  const validateBasketItems = useCallback(
    async (basketItems, references) => {
      const initialErrors = {};
      const defaultCheckoutList = [];

      for (const basketItem of basketItems) {
        const shipment = basketItem.shipment;
        const itemReferences = get(
          references,
          `[${basketItem.basketItemId}]`,
          {}
        );

        checkPickupLocationStatus(
          basketItem,
          shipment,
          itemReferences,
          initialErrors
        );

        const errors = validateFullShipmentSync(
          shipment,
          itemReferences,
          countries
        );

        validateNetwork(shipment, itemReferences, errors);
        await validateCollectionDate(shipment, errors);

        if (errors?.length !== 0) {
          set(initialErrors, basketItem.basketItemId, {
            ...initialErrors[basketItem.basketItemId],
            basketErrors: errors,
          });
        }

        if (errors.length === 0 && !initialErrors[basketItem.basketItemId]) {
          defaultCheckoutList.push(basketItem.basketItemId);
        }
      }

      return { errors: initialErrors, checkoutList: defaultCheckoutList };
    },
    [
      countries,
      checkPickupLocationStatus,
      validateNetwork,
      validateCollectionDate,
    ]
  );

  const loadReferences = useCallback(
    async ({ basketId }) => {
      const references = await dispatch(
        BasketViewSlice.actions.fetchBasketReferences(basketId)
      ).unwrap();

      const { errors, checkoutList } = await validateBasketItems(
        basketItems,
        references
      );

      setErrors(errors);
      dispatch(BasketViewSlice.actions.setCheckoutList(checkoutList));

      return references;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [basketItems]
  );

  const checkoutBasketItems = useMemo(
    () => basketItems.filter(item => checkoutList.includes(item.basketItemId)),
    [basketItems, checkoutList]
  );

  const handleCheckoutSubmit = useCallback(
    async payload => {
      try {
        overlay.show();

        const recaptchaToken = await executeRecaptchaV3('CHECKOUT');

        const checkoutListItems = basketItems.filter(basketItem =>
          checkoutList.includes(basketItem.basketItemId)
        );
        const { errors } = await validateBasketItems(
          checkoutListItems,
          references
        );

        if (keys(errors)?.length !== 0) {
          return snackbar.showError({
            // TODO: put correct message later
            message: 'There is an issue with one or more of your shipments',
          });
        }

        const billingBasketItem = checkoutListItems.find(
          ({ basketItemId }) => basketItemId === selectedBillingBasketItem
        )?.shipment;

        const purchaseData = {
          paymentNonce: payload.nonce,
          vendorRef: payload.type,
          voucherId: voucher && voucher.voucherId,
          billingAddress: getBillingAddress(
            get(billingBasketItem, SHIPMENT_FIELDS.COLLECTION_ADDRESS.KEY, {}),
            get(
              billingBasketItem,
              SHIPMENT_FIELDS.COLLECTION_CONTACT_DETAILS.KEY,
              {}
            )
          ),
        };
        const { orderId, shipments } = await dispatch(
          BasketActions.checkout({
            basketItemIds: checkoutList,
            purchaseData,
            recaptchaToken,
          })
        ).unwrap();

        if (shipments.length === 1) {
          snackbar.showSuccess({
            // put correct message later
            message: 'Shipment have been successfully created',
          });

          // navigate to shipments/:shipmentId
          navigate(`/shipments/${shipments[0].shipmentId}`, {
            replace: true,
          });
        } else {
          snackbar.showSuccess({
            message: `${shipments.length} shipment(s) have been successfully created`,
          });

          navigate(`/orders/${orderId}`, {
            replace: true,
          });
        }
      } catch (error) {
        // NOTE: This is a temporary solution to handle validation errors for migrated addresses
        if (error.errorType === 'validationError') {
          error.message = STRINGS.PLEASE_VERIFY_BILLING_ADDRESS;
        }
        if (!error.details) {
          error.details = [error];
        }
        error.details.forEach(detail => {
          if (detail.rule === 'IOSS') {
            setOpenIossErrorModal(true);
            setIossRuleBasketItemId(detail.basketItemId);
            return;
          }
          snackbar.showError({
            autoHideDuration: null,
            message: detail.message,
          });
        });
      } finally {
        overlay.hide();
      }
    },
    [
      checkoutList,
      voucher,
      basketItems,
      references,
      getBillingAddress,
      overlay,
      snackbar,
      navigate,
      dispatch,
      selectedBillingBasketItem,
      validateBasketItems,
    ]
  );

  return (
    <Trackable loadId={ANALYTICS.BASKET.LOAD}>
      <ConfirmDialog
        open={!!selectedBasketItem}
        onConfirm={onDeleteBasketItem}
        onClose={() => setSelectedBasketItem(null)}
        title={STRINGS.ADDRESSES_SECTION.DELETE_MODAL.TITLE}
        confirmButtonText={STRINGS.ADDRESSES_SECTION.DELETE_MODAL.DELETE_BUTTON}
        containerStyle={{
          maxWidth: isSmallScreen ? '100%' : '400px',
        }}
      >
        {STRINGS.WANT_TO_REMOVE_$(
          STRINGS.PARCEL_TO_$(receiverContactName, parcels.length)
        )}
      </ConfirmDialog>
      <Loader
        promiseFn={loadReferences}
        basketId={basketId}
        watch={basketItems}
      >
        {references => (
          <Grid
            sx={{
              background: theme.vars.palette.primary.pageBackground,
              pb: 4,
              flexGrow: 1,
            }}
          >
            <Grid
              container
              sx={{
                justifyContent: 'center',
              }}
            >
              <Grid>
                <Typography
                  variant='h2'
                  component='h1'
                  sx={{ m: 2 }}
                  color='primary.main'
                >
                  {STRINGS.YOUR_CART}
                </Typography>
              </Grid>
            </Grid>

            <Grid container alignItems='center' justifyContent='center'>
              {basketItems.length ? (
                <Grid
                  container
                  spacing={2}
                  sx={{ mt: 0 }}
                  justifyContent='center'
                  size={{
                    xs: 12,
                    md: 8,
                  }}
                >
                  <Grid
                    size={{
                      xs: 12,
                      md: 8,
                    }}
                  >
                    <BasketItemsGroupsList
                      basketItems={basketItems}
                      basketItemComponent={({ basketItem }) => {
                        const isInCheckoutList = includes(
                          checkoutList,
                          basketItem.basketItemId
                        );

                        return (
                          <BasketCard
                            references={references[basketItem.basketItemId]}
                            key={basketItem.basketItemId}
                            basketItem={basketItem}
                            onRemove={() => setSelectedBasketItem(basketItem)}
                            isInCheckoutList={isInCheckoutList}
                            onAddToCheckout={onAddToCheckout}
                            onRemoveFromCheckout={onRemoveFromCheckout}
                            errors={errors}
                            isOnlyItem={basketItems.length === 1}
                            isOnlyItemInCheckout={
                              checkoutList.length === 1 && isInCheckoutList
                            }
                          />
                        );
                      }}
                    />
                    {displayVoucher && isSmallScreen && (
                      <Voucher voucher={voucher} setVoucher={setVoucher} />
                    )}
                    {!!checkoutBasketItems.length && (
                      <BillingAddress
                        defaultBillingAddress={defaultBillingAddressBook}
                        items={checkoutBasketItems}
                        selectedAddressId={selectedBillingBasketItem}
                        setSelectedAddressId={setSelectedBillingBasketItem}
                      />
                    )}
                    <SalesConditions
                      onAcceptTerms={setAcceptTerms}
                      salesConditionsLink={termsLink}
                      isAvailableProhibitedCondition
                    />
                    <InformationCollecting />
                  </Grid>
                  <StyledGrid
                    size={{
                      xs: 12,
                      md: 4,
                    }}
                    sx={{ mt: { xs: 0, md: 7 }, p: 0 }}
                  >
                    <SummaryDetails
                      price={checkoutListPrice}
                      basketItemsWithPrice={basketItemsWithPrice}
                      showMoreDetails={false}
                    />
                    {displayVoucher && !isSmallScreen && (
                      <Voucher voucher={voucher} setVoucher={setVoucher} />
                    )}
                    <PurchaseWidget
                      basketId={basketId}
                      threeDSecure={threeDSecureData}
                      amount={checkoutListPrice.totalIncVatAmount.toString()}
                      handleCheckout={handleCheckoutSubmit}
                      sx={{ width: '100%' }}
                      disabled={isCheckoutButtonDisabled}
                    >
                      {checkoutList.length > 0
                        ? STRINGS.PROCESS_$_SHIPMENT(checkoutList.length)
                        : STRINGS.PROCESS_TO_CHECKOUT}
                    </PurchaseWidget>
                  </StyledGrid>
                </Grid>
              ) : (
                <Typography sx={{ mt: 1, color: 'text.secondary' }}>
                  {STRINGS.EMPTY_CARD.toUpperCase()}
                </Typography>
              )}
            </Grid>
          </Grid>
        )}
      </Loader>
      <IossErrorModal
        open={openIossErrorModal}
        basketItemId={IossRuleBasketItemId}
        handleClose={() => setOpenIossErrorModal(false)}
      />
    </Trackable>
  );
};

export default Basket;
